import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Users, X, ArrowLeft, Send, MessagesSquare } from "lucide-react";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { usePresence, PresenceDot, STATUS, MY_CHOICES, setMyStatus, lastSeenText } from "@/lib/presence";

const CHAT_POLL_MS = 10000;

/**
 * Header button + side panel: who is online, in a meeting, in the showroom, on site, away or offline, your own
 * status, and a quick 1:1 chat (the same direct messages as Discussions). Mounting it keeps this tab's heartbeat going.
 */
export default function TeamPanel() {
  const { user, canDo } = useAuth();
  const { rows } = usePresence();
  const [open, setOpen] = useState(false);
  const [chatWith, setChatWith] = useState(null);
  const canChat = canDo("discussions", "view");
  const me = rows.find((r) => r.me);
  const others = rows.filter((r) => !r.me);
  const around = others.filter((r) => r.status !== "offline").length;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (!user) return null;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="relative lx-icon-btn p-2 rounded-full hover:bg-black/5"
              aria-label={`Team: ${around} colleague${around === 1 ? "" : "s"} around`} data-testid="team-button">
        <Users size={18} />
        {around > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-emerald-500 text-white text-[10px] font-bold grid place-items-center">
            {around}
          </span>
        )}
      </button>
      {open && (
        <div className="fixed inset-0 z-[60] flex justify-end bg-black/30" onClick={() => setOpen(false)}>
          <aside role="dialog" aria-label="Team" onClick={(e) => e.stopPropagation()}
                 className="w-full max-w-sm h-full bg-[var(--color-surface,#fff)] shadow-2xl flex flex-col" data-testid="team-panel">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-[var(--color-border,#e5e7eb)]">
              {chatWith && <button type="button" onClick={() => setChatWith(null)} aria-label="Back to team" className="p-1 rounded hover:bg-black/5"><ArrowLeft size={16} /></button>}
              <h2 className="font-semibold text-sm flex-1 truncate">{chatWith ? chatWith.name : "Team"}</h2>
              {chatWith && <span className="text-xs text-[var(--color-text-muted,#6b7280)]">{STATUS[chatWith.status]?.label}</span>}
              <button type="button" onClick={() => setOpen(false)} aria-label="Close" className="p-1 rounded hover:bg-black/5"><X size={16} /></button>
            </div>
            {chatWith ? <QuickChat other={chatWith} me={user} /> : (
              <>
                <div className="px-4 py-3 border-b border-[var(--color-border,#e5e7eb)] flex items-center gap-2">
                  <PresenceDot status={me?.status || "online"} />
                  <label htmlFor="my-status" className="text-xs text-[var(--color-text-muted,#6b7280)]">My status</label>
                  <select id="my-status" value={me?.manual || ""} onChange={(e) => setMyStatus(e.target.value)}
                          className="ml-auto text-sm px-2 py-1 rounded-md border border-[var(--color-border,#e5e7eb)] bg-white" data-testid="my-status">
                    {MY_CHOICES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <ul className="flex-1 overflow-y-auto">
                  {others.length === 0 && <li className="px-4 py-6 text-sm text-center text-[var(--color-text-muted,#6b7280)]">No colleagues with their own login yet (shared role logins have no status).</li>}
                  {others.map((r) => (
                    <li key={r.user_id}>
                      <button type="button" disabled={!canChat} onClick={() => setChatWith(r)}
                              className="w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-black/[0.03] disabled:cursor-default"
                              data-testid={`team-member-${r.user_id}`}>
                        <span className="relative shrink-0">
                          <span className="w-9 h-9 rounded-full grid place-items-center text-xs font-bold text-white" style={{ background: r.color || "#3A3F3A" }}>
                            {(r.icon || r.name || "?").slice(0, 2).toUpperCase()}
                          </span>
                          <PresenceDot status={r.status} className="absolute -bottom-0.5 -right-0.5" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-medium truncate">{r.name}</span>
                          <span className="block text-xs text-[var(--color-text-muted,#6b7280)]">
                            {r.status === "offline" ? lastSeenText(r.last_seen) : r.label}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
                {canChat && (
                  <Link to="/discussions" onClick={() => setOpen(false)}
                        className="m-3 inline-flex items-center justify-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-[var(--color-border,#e5e7eb)] hover:bg-black/[0.03]">
                    <MessagesSquare size={14} /> Open Discussion Board
                  </Link>
                )}
              </>
            )}
          </aside>
        </div>
      )}
    </>
  );
}

function QuickChat({ other, me }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottom = useRef(null);
  const load = () => api.get(`/discussions/dm/${other.user_id}`, { skipCache: true }).then(({ data }) => setMsgs(data || [])).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, CHAT_POLL_MS);
    return () => clearInterval(t);
  }, [other.user_id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { bottom.current?.scrollIntoView({ block: "end" }); }, [msgs.length]);

  const send = async () => {
    const body = text.trim();
    if (!body || sending) return;
    setSending(true);
    try { await api.post(`/discussions/dm/${other.user_id}`, { text: body }); setText(""); await load(); }
    finally { setSending(false); }
  };

  return (
    <>
      <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-black/[0.02]" data-testid="quick-chat">
        {msgs.length === 0 && <div className="text-sm text-center text-[var(--color-text-muted,#6b7280)] py-8">No messages yet. Say hello.</div>}
        {msgs.map((m) => {
          const mine = m.author_id === me.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] px-3 py-2 text-sm whitespace-pre-wrap rounded-2xl ${mine ? "bg-[var(--color-primary,#2563eb)] text-white rounded-br-sm" : "bg-white border rounded-bl-sm"}`}>
                {m.text}
                <div className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-[var(--color-text-muted,#6b7280)]"}`}>{fmtDateTime(m.created_at)}</div>
              </div>
            </div>
          );
        })}
        <div ref={bottom} />
      </div>
      <div className="p-3 border-t border-[var(--color-border,#e5e7eb)] flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
               placeholder={`Message ${other.name}`} aria-label={`Message ${other.name}`}
               className="flex-1 min-w-0 border border-[var(--color-border,#e5e7eb)] rounded-lg px-3 py-2 text-sm" data-testid="quick-chat-input" />
        <button type="button" onClick={send} disabled={sending || !text.trim()} className="btn-primary px-3 disabled:opacity-60" aria-label="Send" data-testid="quick-chat-send"><Send size={15} /></button>
      </div>
    </>
  );
}
